/**
 * MsrAvailability — "the days I can work" (business lead review, 2026-10-27).
 *
 * The reps are part-time: the manager needs the days they are AVAILABLE, not time off. The rep taps days on the
 * calendar itself (green = I can work), taps again to remove, and Saves. No add button, no range picker. No "calendar
 * confirmed through". Days are saved by msr_set_available_days (migration 1150): the rep's own positions only, this
 * month or one of the next 12, never a day before today.
 *
 * Bug 2026-10-30 (rep report): owner R2 showed ONLY next month, so on Oct 30 a rep saw November and could not see
 * whether they work today or tomorrow. The page now opens on THIS month (today ringed, blue dots on the days they are
 * scheduled) and steps month by month, this month .. +12. The availability prompt's ?month= link still opens the month
 * it names. Changing month is blocked while there are unsaved taps (Save or Undo first), so a draft never spans months.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MsrTabs from "../../components/msr/MsrTabs";
import AvailabilityMonthCalendar from "../../components/msr/AvailabilityMonthCalendar";
import UnsavedChangesGuard from "../../components/common/UnsavedChangesGuard";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { supabase } from "../../services/supabase";
import {
  getEventDaysFor,
  getMsrSchedule,
  getMyCurrentSalesPositionIds,
} from "../../services/msrPortal.service";
import {
  AVAILABILITY_MONTHS_AHEAD,
  addMonths,
  availabilityPageMonth,
  centralToday,
  getMyAvailableDays,
  monthEnd,
  monthLabel,
  monthStart,
  msrErrorText,
  saveAvailableDays,
} from "../../services/msrFeedback.service";
import { joinDays } from "../../services/timeOffConflicts";
import { expandEventDays, groupEventDays } from "../../utils/eventServiceDays";

const MsrAvailability: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const today = useMemo(() => centralToday(), []);
  // Bug 2026-10-30: open on THIS month (today visible); ?month=YYYY-MM (the prompt's link) picks another in range.
  const [searchParams, setSearchParams] = useSearchParams();
  const month = availabilityPageMonth(searchParams.get("month"), today);
  const firstMonth = monthStart(today);
  const lastMonth = addMonths(firstMonth, AVAILABILITY_MONTHS_AHEAD);

  const { data: positionIds = [], isLoading: posLoading } = useQuery({
    queryKey: ["msr-my-position-ids"],
    queryFn: getMyCurrentSalesPositionIds,
  });
  const [positionChoice, setPositionChoice] = useState("");
  const positionId = positionChoice || positionIds[0] || "";
  const { data: positionNames = {} } = useQuery<Record<string, string>>({
    queryKey: ["msr-position-names", positionIds],
    queryFn: async () => {
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name")
        .in("id", positionIds);
      return Object.fromEntries(
        (data ?? []).map((p) => [p.id, p.assigned_name || p.title || p.id]),
      );
    },
    enabled: positionIds.length > 1,
  });

  const { data: saved = [], isLoading: daysLoading } = useQuery({
    queryKey: ["msr-available-days", positionId, month],
    queryFn: () => getMyAvailableDays([positionId], month, monthEnd(month)),
    enabled: !!positionId,
  });
  const savedSet = useMemo(() => new Set(saved.map((r) => r.day)), [saved]);

  // Blue dots: the days the rep is on an event's Sales Reps list (same schedule as the Schedule tab).
  // The window reaches the end of the month on screen (a later month would otherwise show no dots); 90 days minimum,
  // as before. Days are counted from today, so this is a whole number >= 90.
  const windowDays = Math.max(
    90,
    Math.round(
      (Date.parse(`${monthEnd(month)}T12:00:00Z`) -
        Date.parse(`${today}T12:00:00Z`)) /
        86_400_000,
    ) + 1,
  );
  const { data: events = [] } = useQuery({
    queryKey: ["msr-availability-events", windowDays],
    queryFn: () => getMsrSchedule(false, windowDays),
    enabled: positionIds.length > 0,
  });
  const eventIds = useMemo(
    () => events.map((e) => e.event_id).sort(),
    [events],
  );
  const { data: dayRows } = useQuery({
    queryKey: ["msr-schedule-event-days", eventIds],
    queryFn: () => getEventDaysFor(eventIds),
    enabled: eventIds.length > 0,
  });
  const scheduled = useMemo(() => {
    const byEvent = groupEventDays(dayRows ?? []);
    return new Set(
      events
        .flatMap((e) =>
          expandEventDays(e, byEvent.get(e.event_id), e.sales_rep_days),
        )
        .map((d) => d.date)
        .filter(Boolean) as string[],
    );
  }, [events, dayRows]);

  // Tapping a day changes the draft; Save writes it. The draft starts as what is saved.
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [confirmText, setConfirmText] = useState<string | null>(null);
  useEffect(() => {
    setDraft(null);
  }, [positionId, month]);
  const shown = draft ?? savedSet;
  const dirty =
    draft !== null &&
    (draft.size !== savedSet.size || [...draft].some((d) => !savedSet.has(d)));

  const onDayClick = (day: string) => {
    const next = new Set(shown);
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    setDraft(next);
  };

  const saveMut = useMutation({
    mutationFn: () =>
      saveAvailableDays(
        positionId,
        month,
        [...shown]
          .filter((d) => d >= today && d >= month && d <= monthEnd(month))
          .sort(),
      ),
    onSuccess: (r) => {
      showToast(
        "success",
        `Saved. ${r.days_in_month} day${r.days_in_month === 1 ? "" : "s"} in ${monthLabel(month)}.`,
      );
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ["msr-available-days"] });
      queryClient.invalidateQueries({ queryKey: ["msr-availability-prompt"] });
    },
    onError: (e) => showToast("error", msrErrorText(e, "Could not save")),
  });

  const save = () => {
    // Warn (never block): you're on an event's Sales Reps list on a day you did not mark.
    const missing = [...scheduled]
      .filter(
        (d) =>
          d >= today && d >= month && d <= monthEnd(month) && !shown.has(d),
      )
      .sort();
    if (missing.length) {
      setConfirmText(
        `You're scheduled to work ${joinDays(missing)} but didn't mark ${missing.length > 1 ? "those days" : "that day"} as available. Tell your manager so they can change who works. Save anyway?`,
      );
      return;
    }
    saveMut.mutate();
  };

  if (posLoading) {
    return <div className="p-4 text-sm text-gray-500">Loading…</div>;
  }
  if (positionIds.length === 0) {
    return (
      <div className="p-4 space-y-3 max-w-xl mx-auto">
        <h1 className="text-xl font-semibold text-gray-900">My Availability</h1>
        <MsrTabs active="availability" />
        <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
          You're not linked to a sales position yet. Ask your manager to set it
          up, then you can add the days you can work here.
        </div>
      </div>
    );
  }

  const goToMonth = (m: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("month", m.slice(0, 7));
        return next;
      },
      { replace: true },
    );
  };
  const canPrev = month > firstMonth && !dirty;
  const canNext = month < lastMonth && !dirty;
  const navBtn =
    "min-h-[40px] min-w-[40px] rounded-md border border-gray-300 bg-white px-3 text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-30";

  const count = [...shown].filter(
    (d) => d >= month && d <= monthEnd(month),
  ).length;

  return (
    <div className="p-4 space-y-4 max-w-xl mx-auto">
      <ConfirmDialog
        isOpen={confirmText !== null}
        onClose={() => setConfirmText(null)}
        onConfirm={() => {
          setConfirmText(null);
          saveMut.mutate();
        }}
        title="You're scheduled on a day you didn't mark"
        message={confirmText ?? ""}
        confirmText="Save anyway"
        variant="warning"
      />
      <UnsavedChangesGuard
        when={dirty && !saveMut.isPending}
        message="Your availability isn't saved yet. Leave and lose it?"
      />
      <h1 className="text-xl font-semibold text-gray-900">My Availability</h1>
      <MsrTabs active="availability" />

      <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Previous month"
            data-testid="msr-availability-prev"
            onClick={() => goToMonth(addMonths(month, -1))}
            disabled={!canPrev}
            className={navBtn}
          >
            ‹
          </button>
          <div
            className="text-base font-semibold"
            data-testid="msr-availability-month"
          >
            {monthLabel(month)}
          </div>
          <button
            type="button"
            aria-label="Next month"
            data-testid="msr-availability-next"
            onClick={() => goToMonth(addMonths(month, 1))}
            disabled={!canNext}
            className={navBtn}
          >
            ›
          </button>
        </div>
        {month !== firstMonth && (
          <button
            type="button"
            onClick={() => goToMonth(firstMonth)}
            disabled={dirty}
            className="mt-1 text-xs font-medium text-green-800 underline disabled:opacity-40"
            data-testid="msr-availability-this-month"
          >
            Back to this month
          </button>
        )}
        {dirty && (
          <div className="mt-1 text-xs text-green-800">
            Save or undo your changes to see another month.
          </div>
        )}
        <div className="mt-2">
          Tap each day you <strong>can work</strong> — it turns green. Tap it
          again to remove it. Then press <strong>Save</strong>.
        </div>
      </div>

      {positionIds.length > 1 && (
        <select
          value={positionId}
          onChange={(e) => setPositionChoice(e.target.value)}
          disabled={dirty}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          aria-label="Sales position"
        >
          {positionIds.map((id) => (
            <option key={id} value={id}>
              {positionNames[id] ?? id}
            </option>
          ))}
        </select>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-2">
        <AvailabilityMonthCalendar
          month={month}
          available={shown}
          editable={!daysLoading}
          minDay={today}
          today={today}
          scheduled={scheduled}
          onDayClick={onDayClick}
        />
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-green-500" /> I can
          work ({count} day{count === 1 ? "" : "s"})
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-600" />{" "}
          I'm scheduled on an event
        </span>
        {month === firstMonth && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded ring-2 ring-gray-900" />{" "}
            Today
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saveMut.isPending}
          className="flex-1 min-h-[48px] rounded-lg bg-blue-600 px-4 text-base font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
          data-testid="msr-save-availability"
        >
          {saveMut.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(null)}
            disabled={saveMut.isPending}
            className="min-h-[48px] rounded-lg border border-gray-300 bg-white px-4 text-base font-medium text-gray-700 hover:bg-gray-50"
          >
            Undo changes
          </button>
        )}
      </div>
    </div>
  );
};

export default MsrAvailability;
